import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CaretLeft, CaretRight, Car, ChartDonut, Crown, CurrencyEur, Door, DotsThree, GearSix, HouseLine, List, MagnifyingGlass, PencilSimple, Plus, Ruler,
  SquaresFour, Storefront, Trash, Warehouse, type Icon,
} from '@phosphor-icons/react';
import {
  BestaetigenDialog, FlipCard, ImmoButton, LeerZustand, MenueEintrag, Popover, SegmentedControl, ToastPill, useToastPill,
} from '../../../../../components/immobilien/ui';
import { supabase } from '../../../../../lib/supabase/client';
import { deDatum, type EinheitArt, type EinheitUebersicht } from '../../../../../lib/immobilien/objektDetail';
import { eur, initialen, qm } from '../../../../../lib/immobilien/format';
import { filterAnzahlen, filtereEinheiten, haeuserUebersicht, OHNE_HAUS, type EinheitenFilter } from '../../../../../lib/immobilien/einheitenTab';
import { meldeObjektGeaendert } from '../../../../../lib/immobilien/useObjektKontext';
import { BUCHHALTUNG_FOLGT } from '../../../../../lib/immobilien/hinweise';
import { loeschFehlerText } from '../../../../../lib/immobilien/zimmer';
import { useObjektSeite } from '../ObjektSeitenKontext';
import { NeueEinheitFormular } from './NeueEinheitFormular';
import { NeuesHausFormular } from './NeuesHausFormular';
import { HaeuserVerwaltung } from './HaeuserVerwaltung';

const ART_ICON: Record<EinheitArt, Icon> = { wohnung: Door, gewerbe: Storefront, stellplatz: Car, sonstiges: Warehouse };
const ART_LABEL: Record<EinheitArt, string> = { wohnung: 'Wohnung', gewerbe: 'Gewerbe', stellplatz: 'Stellplatz', sonstiges: 'Sonstiges' };
type Ansicht = 'kacheln' | 'liste';

function statusVon(e: EinheitUebersicht): { label: string; farbe: string; flaeche: string } {
  if (e.vermietet) return { label: 'Vermietet', farbe: 'var(--ok)', flaeche: 'var(--ok-soft)' };
  if (e.status === 'eigennutzung') return { label: 'Eigennutzung', farbe: 'var(--info)', flaeche: 'var(--info-soft)' };
  if (e.status === 'renovierung') return { label: 'Renovierung', farbe: 'var(--ink2)', flaeche: 'var(--fill2)' };
  return { label: 'Leerstand', farbe: 'var(--warn)', flaeche: 'var(--warn-soft)' };
}

/** Einheiten-Tab (README §5.4, Screens 20–22): Häuser → Einheiten, Filter, Kachel/Liste, „Neue Einheit“ als Drehkarte. */
export function EinheitenTab() {
  const { daten, uebersicht, neuLaden, zuEinheiten } = useObjektSeite();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  // Aus dem Objektkopf: ?neu=1 (Neue Einheit), ?neu=haus (Neues Haus), ?haeuser=1 (Häuser verwalten)
  const [formular, setFormular] = useState<'einheit' | 'haus' | 'verwalten' | null>(() =>
    params.get('neu') === '1' ? 'einheit' : params.get('neu') === 'haus' ? 'haus' : params.get('haeuser') === '1' ? 'verwalten' : null);
  useEffect(() => {
    if (!params.has('neu') && !params.has('haeuser')) return;
    const p = new URLSearchParams(params);
    p.delete('neu');
    p.delete('haeuser');
    setParams(p, { replace: true });
  }, [params, setParams]);
  const [filter, setFilter] = useState<EinheitenFilter>('alle');
  const [suche, setSuche] = useState('');
  const [ansicht, setAnsicht] = useState<Ansicht>('kacheln');
  const [neuId, setNeuId] = useState<string | null>(null);
  const [loeschen, setLoeschen] = useState<EinheitUebersicht | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [toast, zeige] = useToastPill();

  const o = daten.objekt;
  const mitHaeusern = daten.haeuser.length > 0;
  const haus = params.get('haus');
  const haeuser = useMemo(() => haeuserUebersicht(daten.haeuser, uebersicht), [daten.haeuser, uebersicht]);
  const imHaus = useMemo(() => (haus ? uebersicht.filter(e => (haus === OHNE_HAUS ? !e.hausId || !daten.haeuser.some(h => h.id === e.hausId) : e.hausId === haus)) : uebersicht), [haus, uebersicht, daten.haeuser]);
  const hausName = haus ? haeuser.find(h => h.id === haus)?.bezeichnung ?? 'Haus' : null;
  const zeigeHaeuser = mitHaeusern && !haus;
  // #499: SEV je Wohnung (einheit_eigentuemer.sev_status) und Zimmer als Zusatz in der Kurzzeile
  const sev = useMemo(() => new Set(daten.weg.filter(w => (w.sev_status ?? '').trim()).map(w => w.einheit_id)), [daten.weg]);
  const zusatz = (e: EinheitUebersicht) =>
    `${e.zimmerAnzahl > 0 ? ` · WG ${e.zimmerVermietet}/${e.zimmerAnzahl} Zi. vermietet` : ''}${sev.has(e.id) ? ' · SEV' : ''}`;
  // #514: Eigentümer je Einheit (WEG: einheit_eigentuemer, laufende Hauptzeile) – in Kachel, Liste und Suche
  const eigentuemer = useMemo(() => new Map(daten.weg.filter(w => w.name.trim()).map(w => [w.einheit_id, w.name.trim()])), [daten.weg]);
  const mitEigentuemer = eigentuemer.size > 0;
  const spalten = `minmax(0,1.6fr) minmax(0,1.6fr) ${mitEigentuemer ? 'minmax(0,1.6fr) ' : ''}minmax(0,0.8fr) minmax(0,0.9fr) minmax(0,0.9fr) 36px`;
  const sichtbar = filtereEinheiten(imHaus, filter, suche, eigentuemer);
  const anzahlen = filterAnzahlen(imHaus);
  const summe = imHaus.reduce((s, e) => s + e.gesamt, 0);
  const flaeche = imHaus.reduce((s, e) => s + e.flaeche, 0);
  const vermietet = imHaus.filter(e => e.vermietet);
  const kaltQm = (() => { const f = vermietet.reduce((s, e) => s + e.flaeche, 0); return f > 0 ? vermietet.reduce((s, e) => s + e.kalt, 0) / f : null; })();
  const belegung = imHaus.length ? Math.round((vermietet.length / imHaus.length) * 100) : 0;

  const nachAenderung = () => { neuLaden('einheiten', 'mieter', 'haeuser', 'weg'); meldeObjektGeaendert(o.id); };
  const setzeHaus = (id: string | null) => setParams(id ? new URLSearchParams({ haus: id }) : new URLSearchParams());

  const haeuserVerwalten = () => setFormular(f => (f === 'verwalten' ? null : 'verwalten'));

  /** „Einheit bearbeiten“ im neuen Design – Bereich auf der Einheit-Seite. */
  const bearbeiten = (e: EinheitUebersicht) =>
    navigate(`/admin/objekte/${encodeURIComponent(o.id)}/einheiten/${encodeURIComponent(e.id)}?bereich=edit`);

  const bestaetigeLoeschen = async () => {
    if (!loeschen) return;
    setLaeuft(true);
    const { error } = await supabase.from('objekt_einheiten').delete().eq('id', loeschen.id);
    setLaeuft(false);
    if (error) { zeige(`Löschen fehlgeschlagen: ${loeschFehlerText(error.message)}`, true); return; }
    zeige(`${loeschen.bezeichnung} gelöscht`);
    setLoeschen(null);
    nachAenderung();
  };

  const aktionen = (e: EinheitUebersicht) => (
    <Popover label={`Aktionen für ${e.bezeichnung}`} breite={190}
      ausloeser={({ offen, umschalten, ref }) => (
        <button ref={ref} type="button" aria-label={`Aktionen für ${e.bezeichnung}`} aria-haspopup="menu" aria-expanded={offen}
          onClick={ev => { ev.stopPropagation(); umschalten(); }} className="immo-hover-flaeche flex items-center justify-center cursor-pointer"
          style={{ width: 32, height: 32, borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--ink2)' }}>
          <DotsThree weight="bold" size={18} />
        </button>
      )}>
      {schliessen => (
        <>
          <MenueEintrag onClick={() => { schliessen(); bearbeiten(e); }}><PencilSimple size={15} />Bearbeiten</MenueEintrag>
          <MenueEintrag gefahr onClick={() => { schliessen(); setLoeschen(e); }}><Trash size={15} />Löschen</MenueEintrag>
        </>
      )}
    </Popover>
  );

  const umschalten = (ziel: 'einheit' | 'haus') => setFormular(f => (f === ziel ? null : ziel));
  const titel = haus ? hausName : mitHaeusern ? 'Häuser & Einheiten' : 'Einheiten';

  return (
    <section className="flex flex-col" style={{ gap: 12 }} aria-label="Einheiten">
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <div className="flex justify-between items-center flex-wrap" style={{ gap: 12 }}>
        <div className="flex items-baseline flex-wrap" style={{ gap: 10 }}>
          {haus && (
            <button type="button" onClick={() => setzeHaus(null)} className="flex items-center cursor-pointer self-center"
              style={{ height: 30, padding: '0 12px 0 8px', borderRadius: 15, border: 'none', gap: 5, background: 'var(--fill2)', fontSize: 12, fontWeight: 700, color: 'var(--ink)' }}>
              <CaretLeft weight="bold" size={12} />Alle Häuser
            </button>
          )}
          <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>{titel}</span>
          <span className="immo-zahl" style={{ fontSize: 13, color: 'var(--ink3)' }}>
            {imHaus.length} {imHaus.length === 1 ? 'Einheit' : 'Einheiten'} · {eur(summe, { vorzeichen: true })} mtl.
          </span>
        </div>
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          {!zeigeHaeuser && (
            <button type="button" onClick={() => umschalten('einheit')} aria-expanded={formular === 'einheit'} className="flex items-center cursor-pointer"
              style={{ height: 38, padding: '0 14px', borderRadius: 19, border: 'none', gap: 6, fontSize: 13, fontWeight: 700,
                background: formular === 'einheit' ? 'var(--fill2)' : 'var(--acc-soft)', color: formular === 'einheit' ? 'var(--ink)' : 'var(--acc)' }}>
              {formular === 'einheit' ? <><Plus weight="bold" size={13} style={{ transform: 'rotate(45deg)', transition: 'transform .4s' }} />Abbrechen</> : <><Door weight="bold" size={13} />Neue Einheit</>}
            </button>
          )}
          {mitHaeusern && <ImmoButton groesse={36} icon={GearSix} onClick={haeuserVerwalten} style={{ height: 38, borderRadius: 19 }}>Häuser verwalten</ImmoButton>}
          <ImmoButton art={formular === 'haus' ? 'fuellung' : 'akzent'} groesse={36} icon={Plus} iconGedreht={formular === 'haus'} onClick={() => umschalten('haus')}
            aria-expanded={formular === 'haus'} style={{ height: 38, borderRadius: 19 }}>
            {formular === 'haus' ? 'Abbrechen' : 'Haus / Liegenschaft hinzufügen'}
          </ImmoButton>
        </div>
      </div>

      <FlipCard
        gedreht={formular !== null}
        hinten={formular === 'einheit' ? (
          <NeueEinheitFormular hausId={haus} onAbbrechen={() => setFormular(null)}
            onAngelegt={(id, bez) => { setFormular(null); setNeuId(id); zeige(`${bez} angelegt`); nachAenderung(); }} />
        ) : formular === 'haus' ? (
          <NeuesHausFormular onAbbrechen={() => setFormular(null)} onAngelegt={bez => { setFormular(null); zeige(`${bez} angelegt`); nachAenderung(); }} />
        ) : formular === 'verwalten' ? (
          <HaeuserVerwaltung onSchliessen={() => setFormular(null)} onNeuesHaus={() => setFormular('haus')} onGeaendert={nachAenderung} zeige={zeige} />
        ) : <div />}
        vorne={zeigeHaeuser ? (
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12 }}>
            {haeuser.map(h => (
              <button key={h.id} type="button" onClick={() => setzeHaus(h.id)} className="immo-glas immo-hover-widget flex flex-col text-left cursor-pointer"
                style={{ borderRadius: 26, padding: 18, gap: 14, border: 'none', color: 'var(--ink)' }}>
                <span className="flex items-center" style={{ gap: 12 }}>
                  <span className="flex items-center justify-center" style={{ width: 44, height: 44, borderRadius: 14, background: 'var(--acc-soft)' }}><HouseLine weight="fill" size={22} color="var(--acc)" /></span>
                  <span className="flex flex-col flex-1 min-w-0"><span className="truncate" style={{ fontSize: 16, fontWeight: 800 }}>{h.bezeichnung}</span><span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{h.adresse || 'Adresse wie Objekt'}</span></span>
                  <CaretRight weight="bold" size={14} color="var(--ink3)" />
                </span>
                <span className="grid" style={{ gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 8 }}>
                  {([[h.anzahl, 'Einheiten'], [h.frei, 'frei'], [eur(h.summe, { nachkomma: 0 }), 'mtl.']] as const).map(([w, l]) => (
                    <span key={l} className="flex flex-col"><b className="immo-zahl" style={{ fontSize: 17, fontWeight: 800 }}>{w}</b><span style={{ fontSize: 11, color: 'var(--ink3)' }}>{l}</span></span>
                  ))}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="flex flex-col" style={{ gap: 12 }}>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 10 }}>
              {([[String(imHaus.length), imHaus.length === 1 ? 'Einheit' : 'Einheiten', Door, 'var(--acc)', 'var(--acc-soft)'],
                [`${belegung} %`, belegung === 100 ? 'Voll vermietet' : `${anzahlen.frei} frei`, ChartDonut, 'var(--ok)', 'var(--ok-soft)'],
                [qm(Math.round(flaeche * 100) / 100), 'Gesamtfläche', Ruler, 'var(--info)', 'var(--info-soft)'],
                [kaltQm !== null ? eur(Math.round(kaltQm * 100) / 100) : '–', 'Ø Kalt / m²', CurrencyEur, 'var(--warn)', 'var(--warn-soft)']] as const).map(([w, l, IconKomp, f, b]) => (
                <div key={l} className="immo-glas flex items-center" style={{ borderRadius: 20, padding: 14, gap: 12 }}>
                  <span className="flex items-center justify-center shrink-0" style={{ width: 42, height: 42, borderRadius: 14, background: b }}><IconKomp weight="fill" size={20} color={f} /></span>
                  <span className="flex flex-col min-w-0"><span className="immo-zahl" style={{ fontSize: 20, fontWeight: 800 }}>{w}</span><span className="truncate" style={{ fontSize: 12, color: 'var(--ink2)' }}>{l}</span></span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
              <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
                <SegmentedControl<EinheitenFilter> ariaLabel="Filter" wert={filter} onChange={setFilter} segmente={[
                  { key: 'alle', label: 'Alle', anzahl: anzahlen.alle }, { key: 'vermietet', label: 'Vermietet', anzahl: anzahlen.vermietet },
                  { key: 'frei', label: 'Frei', anzahl: anzahlen.frei }, { key: 'gewerbe', label: 'Gewerbe & Garagen', anzahl: anzahlen.gewerbe },
                ]} />
                <label className="flex items-center" style={{ height: 40, borderRadius: 20, gap: 8, padding: '0 12px', background: 'var(--fill2)', width: 220 }}>
                  <MagnifyingGlass size={15} color="var(--ink3)" aria-hidden />
                  <input value={suche} onChange={x => setSuche(x.target.value)} placeholder={mitEigentuemer ? 'Einheit, Mieter oder Eigentümer' : 'Einheit oder Mieter'} aria-label="Einheiten durchsuchen"
                    className="flex-1 min-w-0" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--ink)' }} />
                </label>
              </div>
              <SegmentedControl<Ansicht> ariaLabel="Ansicht" wert={ansicht} onChange={setAnsicht} hoehe={30}
                segmente={[{ key: 'kacheln', label: 'Kacheln', icon: SquaresFour }, { key: 'liste', label: 'Liste', icon: List }]} />
            </div>

            {uebersicht.length === 0 && (
              <LeerZustand icon={Door} titel="Noch keine Einheiten" text="Lege die erste Wohnung, Garage oder Gewerbeeinheit an.">
                <ImmoButton art="akzent" icon={Plus} onClick={() => setFormular('einheit')}>Neue Einheit</ImmoButton>
              </LeerZustand>
            )}
            {uebersicht.length > 0 && sichtbar.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)', padding: '10px 4px' }}>Keine Einheiten für diesen Filter.</span>}

            {ansicht === 'kacheln' ? (
              <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(250px,1fr))', gap: 12 }}>
                {sichtbar.map(e => {
                  const st = statusVon(e);
                  const IconKomp = ART_ICON[e.art];
                  return (
                    <div key={e.id} role="link" tabIndex={0} onClick={() => zuEinheiten(e.id)} onKeyDown={x => { if (x.key === 'Enter') zuEinheiten(e.id); }}
                      className="immo-glas immo-hover-widget flex flex-col cursor-pointer"
                      style={{ borderRadius: 24, padding: 16, gap: 14, boxShadow: neuId === e.id ? 'inset 0 0 0 2px var(--ok), var(--glass-sh)' : undefined }}>
                      <div className="flex items-center" style={{ gap: 10 }}>
                        <span className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 13, background: st.flaeche }}><IconKomp weight="fill" size={19} color={st.farbe} /></span>
                        <span className="flex flex-col flex-1 min-w-0"><span className="truncate" style={{ fontSize: 15, fontWeight: 800 }}>{e.bezeichnung}</span>
                          <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{ART_LABEL[e.art]}{e.flaeche > 0 ? ` · ${qm(e.flaeche)}` : ''}{e.zimmer && !e.zimmerAnzahl ? ` · ${e.zimmer} Zi.` : ''}{zusatz(e)}</span></span>
                        <span className="flex items-center whitespace-nowrap" style={{ height: 22, padding: '0 8px', borderRadius: 11, gap: 5, fontSize: 10, fontWeight: 800, background: st.flaeche, color: st.farbe }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.farbe }} />{st.label}
                        </span>
                      </div>
                      <div className="flex items-center" style={{ gap: 10 }}>
                        <span className="flex items-center justify-center shrink-0" style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--fill2)', fontSize: 12, fontWeight: 700, color: 'var(--ink2)' }}>
                          {e.vermietet ? initialen(e.mieter[0]) : '–'}
                        </span>
                        <span className="flex flex-col flex-1 min-w-0"><span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{e.vermietet ? e.mieter.join(', ') : 'Kein Mieter'}</span>
                          <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{mitEigentuemer && e.vermietet ? 'Mieter · ' : ''}{e.seit ? `seit ${deDatum(e.seit)}` : e.vermietet ? 'Mietbeginn unbekannt' : 'frei'}</span></span>
                        <div onClick={x => x.stopPropagation()}>{aktionen(e)}</div>
                      </div>
                      {mitEigentuemer && (
                        <div className="flex items-center" style={{ gap: 10, marginTop: -4 }}>
                          <span className="flex items-center justify-center shrink-0" style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--acc-soft)' }}>
                            <Crown weight="fill" size={15} color="var(--acc)" />
                          </span>
                          <span className="flex flex-col flex-1 min-w-0">
                            <span className="truncate" style={{ fontSize: 13, fontWeight: 700, color: eigentuemer.get(e.id) ? 'var(--ink)' : 'var(--ink3)' }}>{eigentuemer.get(e.id) ?? 'Kein Eigentümer hinterlegt'}</span>
                            <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Eigentümer</span>
                          </span>
                        </div>
                      )}
                      <div className="grid" style={{ gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 8, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
                        {([['Kalt', e.vermietet ? eur(e.kalt, { nachkomma: 0 }) : '–'], ['€/m²', e.vermietet && e.flaeche > 0 ? eur(Math.round((e.kalt / e.flaeche) * 100) / 100) : '–'], ['Gesamt', e.vermietet ? eur(e.gesamt, { nachkomma: 0 }) : '–']] as const).map(([l, w]) => (
                          <span key={l} className="flex flex-col"><span style={{ fontSize: 11, color: 'var(--ink3)' }}>{l}</span><b className="immo-zahl" style={{ fontSize: 14, fontWeight: 800 }}>{w}</b></span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="immo-glas overflow-x-auto" style={{ borderRadius: 24 }}>
                <div style={{ minWidth: mitEigentuemer ? 880 : 720 }}>
                  <div className="grid" style={{ gridTemplateColumns: spalten, gap: 10, padding: '12px 18px', fontSize: 11, fontWeight: 700, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--line)' }}>
                    <span>Einheit</span><span>Mieter</span>{mitEigentuemer && <span>Eigentümer</span>}<span>Fläche</span><span>Gesamt</span><span>Status</span><span />
                  </div>
                  {sichtbar.map(e => {
                    const st = statusVon(e);
                    return (
                      <div key={e.id} onClick={() => zuEinheiten(e.id)} className="immo-hover-flaeche grid items-center cursor-pointer"
                        style={{ gridTemplateColumns: spalten, gap: 10, padding: '10px 18px', borderBottom: '1px solid var(--line)', fontSize: 13,
                          boxShadow: neuId === e.id ? 'inset 0 0 0 2px var(--ok)' : undefined }}>
                        <span className="flex flex-col min-w-0"><b className="truncate" style={{ fontWeight: 700 }}>{e.bezeichnung}</b><span style={{ fontSize: 11, color: 'var(--ink3)' }}>{ART_LABEL[e.art]}{e.etage ? ` · ${e.etage}` : ''}{zusatz(e)}</span></span>
                        <span className="truncate" style={{ color: e.vermietet ? 'var(--ink)' : 'var(--ink3)' }}>{e.vermietet ? e.mieter.join(', ') : '–'}</span>
                        {mitEigentuemer && (
                          <span className="flex items-center min-w-0" style={{ gap: 6, color: eigentuemer.get(e.id) ? 'var(--ink)' : 'var(--ink3)' }}>
                            <Crown weight="fill" size={12} color="var(--acc)" className="shrink-0" /><span className="truncate">{eigentuemer.get(e.id) ?? '–'}</span>
                          </span>
                        )}
                        <span className="immo-zahl">{e.flaeche > 0 ? qm(e.flaeche) : '–'}</span>
                        <span className="immo-zahl" style={{ fontWeight: 700 }}>{e.vermietet ? eur(e.gesamt) : '–'}</span>
                        <span className="flex items-center" style={{ gap: 6, fontSize: 12, fontWeight: 700, color: st.farbe }} title={`Saldo: ${BUCHHALTUNG_FOLGT}`}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: st.farbe }} />{st.label}
                        </span>
                        <div onClick={x => x.stopPropagation()}>{aktionen(e)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      />

      {loeschen && (
        <BestaetigenDialog titel="Einheit löschen?" text="Das kann nicht rückgängig gemacht werden." detail={<>„{loeschen.bezeichnung}“{loeschen.vermietet ? ` – aktuell vermietet an ${loeschen.mieter.join(', ')}` : ''}</>}
          bestaetigenLabel="Löschen" laeuft={laeuft} onBestaetigen={() => { void bestaetigeLoeschen(); }} onAbbrechen={() => setLoeschen(null)} />
      )}
    </section>
  );
}
